import { TenantConfiguration, WorkDescriptionMappingConfig } from 'types/Tenant';

/**
 * Resolve the effective work-description mapping from a config that may carry the
 * new `work_description_mapping` key, the legacy `error_mapping` key, both, or
 * neither.
 *
 * Returns `undefined` when NEITHER key is set — deliberately, not `{ entries: [] }`.
 * The save payload drops `undefined`, so a tenant that never had the block keeps
 * neither key and we avoid injecting an empty block into untouched tenants.
 */
export function coalesceWorkDescriptionMapping(config: {
  work_description_mapping?: WorkDescriptionMappingConfig;
  error_mapping?: WorkDescriptionMappingConfig;
}): WorkDescriptionMappingConfig | undefined {
  if (config.work_description_mapping && config.error_mapping) {
    console.warn('[work_description_mapping] Both keys present; using work_description_mapping');
  }
  return config.work_description_mapping ?? config.error_mapping;
}

/**
 * Drop the legacy `error_mapping` key and bind the mapping to
 * `work_description_mapping` so in-memory state never holds a parallel legacy
 * field. Sets the new key ONLY when a mapping exists, so a config that had
 * neither key keeps neither (no empty-block injection).
 */
export function normalizeWorkDescriptionMappingConfig(
  config: TenantConfiguration,
): TenantConfiguration {
  const { error_mapping: _legacy, ...rest } = config;
  const mapping = coalesceWorkDescriptionMapping(config);
  return mapping ? { ...rest, work_description_mapping: mapping } : rest;
}
